// Web Bluetooth flavour of the app's TempStickBle bridge, so a Chrome/Edge
// session on a computer (or Chrome on Android) can set a sensor up without
// the app. Same call shapes as window.gonative.nativebridge.ble, same wire
// format as the sensor: temp-stick-app-ionic/firmware/esp32s3-ble-sim/PROTOCOL.md.
//
// What the browser API forces to be different:
//  - there is no programmatic scan. connect() opens the browser's device
//    chooser and must be called from a click; `chooser: true` tells callers.
//  - the negotiated MTU isn't exposed, so requests go out in 20-byte packets,
//    which fit any link. Responses arrive however the sensor sized them.
//  - Safari and Firefox have no navigator.bluetooth; webBluetoothAvailable()
//    is false there and the caller falls back to the app / Wi-Fi paths.

const SERVICE_UUID = '7d0a0001-6b1e-4a9c-9e4f-54656d705374';
const REQ_UUID = '7d0a0002-6b1e-4a9c-9e4f-54656d705374';
const RESP_UUID = '7d0a0003-6b1e-4a9c-9e4f-54656d705374';
const INFO_UUID = '7d0a0004-6b1e-4a9c-9e4f-54656d705374';
const STATUS_UUID = '7d0a0005-6b1e-4a9c-9e4f-54656d705374';

const FRAME_FIRST = 0x80;
const FRAME_LAST = 0x40;
const FRAME_SEQ = 0x3f;

const NAME_PREFIX = 'Sensor Setup ';

const REQUEST_PAYLOAD_BYTES = 19;      // + 1 header byte = 20, the payload of the smallest MTU
const MAX_RESPONSE_BYTES = 16384;
const DEFAULT_CONNECT_TIMEOUT_MS = 20000;
const DEFAULT_REQUEST_TIMEOUT_MS = 15000;

export function webBluetoothAvailable() {
  try {
    return typeof navigator !== 'undefined'
      && !!navigator.bluetooth
      && typeof navigator.bluetooth.requestDevice === 'function';
  } catch (e) {
    return false;
  }
}

// Whether this device actually has a usable Bluetooth adapter, on top of the
// browser supporting the API. Chrome/Edge answer through getAvailability();
// a browser without it is treated as having no adapter, so the option only
// appears where it can work.
let availabilityPromise = null;

export function webBluetoothDeviceAvailable() {

  if (!webBluetoothAvailable()) {
    return Promise.resolve(false);
  }

  if (!availabilityPromise) {

    availabilityPromise = (async () => {
      try {
        if (typeof navigator.bluetooth.getAvailability !== 'function') {
          return false;
        }
        return (await navigator.bluetooth.getAvailability()) === true;
      } catch (e) {
        return false;
      }
    })();

  }

  return availabilityPromise;

}

// Why the browser can't offer Bluetooth, for a hint on beta:
//   'no-api'      Safari, Firefox, and anything else without Web Bluetooth
//   'brave'       Brave has the API but ships it switched off
//                 (brave://flags/#brave-web-bluetooth-api)
//   'no-adapter'  the API is there but the machine has no usable adapter
//   null          nothing wrong
export async function webBluetoothUnavailableReason() {

  if (!webBluetoothAvailable()) {
    return 'no-api';
  }

  if (await webBluetoothDeviceAvailable()) {
    return null;
  }

  let brave = false;

  try {
    brave = !!(navigator.brave && typeof navigator.brave.isBrave === 'function' && await navigator.brave.isBrave());
  } catch (e) {
    brave = false;
  }

  return brave ? 'brave' : 'no-adapter';

}

const normalizeSid = value => String(value || '').replace(/[^a-z0-9]/gi, '').toUpperCase();

const withTimeout = (promise, ms, message) => new Promise((resolve, reject) => {
  const timer = setTimeout(() => reject(new Error(message)), ms);
  promise.then(v => { clearTimeout(timer); resolve(v); }, e => { clearTimeout(timer); reject(e); });
});

class WebBluetoothBridge {

  chooser = true;

  device = null;
  reqChar = null;
  respChar = null;
  infoChar = null;
  statusChar = null;

  ready = false;
  sensorId = '';
  mac = '';
  name = '';

  queue = [];
  active = null;
  nextId = 1;
  rx = { chunks: [], size: 0, open: false, seq: 0 };

  // Every method takes the same { ..., callback } options object as the
  // native bridge and also returns a promise of the same result.
  deliver(opts, result) {
    if (opts && typeof opts.callback === 'function') {
      try {
        opts.callback(result);
      } catch (e) {
        console.log('ble callback error', e);
      }
    }
    return result;
  }

  isAvailable(opts) {
    const available = webBluetoothAvailable();
    const result = {
      available,
      enabled: available,
      permitted: true,
      locationEnabled: true,
      connected: this.ready,
      sensorId: this.sensorId,
      chooser: true
    };
    return Promise.resolve(this.deliver(opts, result));
  }

  enable(opts) {
    const available = webBluetoothAvailable();
    return Promise.resolve(this.deliver(opts, { success: available, enabled: available }));
  }

  state(opts) {
    return Promise.resolve(this.deliver(opts, {
      connected: this.ready,
      phase: this.ready ? 'ready' : 'idle',
      mtu: 0,
      sensorId: this.sensorId,
      address: this.device ? this.device.id : '',
      name: this.name
    }));
  }

  startScan(opts) {
    return this.scan(opts);
  }

  stopScan(opts) {
    return Promise.resolve(this.deliver(opts, { success: true, wasScanning: false }));
  }

  // The browser owns discovery: the chooser opened by connect() is the scan.
  scan(opts) {
    return Promise.resolve(this.deliver(opts, {
      success: false,
      code: 'CHOOSER',
      devices: [],
      message: 'This browser picks the sensor from its own list; use connect()'
    }));
  }

  async connect(opts = {}) {

    let result;

    try {
      result = await this.doConnect(opts);
    } catch (e) {
      await this.teardown('connect_failed', false);
      result = {
        success: false,
        code: this.codeFor(e),
        message: (e && e.message) ? e.message : String(e)
      };
    }

    return this.deliver(opts, result);

  }

  codeFor(e) {
    const name = e && e.name;
    if (name === 'NotFoundError') return 'CANCELLED';
    if (name === 'SecurityError') return 'PERMISSION';
    if (name === 'NotSupportedError') return 'UNAVAILABLE';
    if (name === 'NetworkError') return 'GATT_ERROR';
    if (/timed out/i.test((e && e.message) || '')) return 'TIMEOUT';
    return 'GATT_ERROR';
  }

  async doConnect(opts) {

    if (!webBluetoothAvailable()) {
      return { success: false, code: 'UNAVAILABLE', message: 'This browser has no Bluetooth support' };
    }

    await this.teardown('reconnect', false);

    const wantSid = normalizeSid(opts.sensorId);
    const timeoutMs = opts.timeoutMs || DEFAULT_CONNECT_TIMEOUT_MS;

    // With a known id, list that sensor first; any Temp Stick advertising
    // the setup service is still selectable and the id is verified below.
    const filters = [];
    if (opts.sensorId) {
      filters.push({ namePrefix: NAME_PREFIX + String(opts.sensorId).trim() });
    }
    filters.push({ services: [SERVICE_UUID] });
    filters.push({ namePrefix: NAME_PREFIX });

    let device;

    try {
      device = await navigator.bluetooth.requestDevice({
        filters,
        optionalServices: [SERVICE_UUID]
      });
    } catch (e) {
      if (e && e.name === 'NotFoundError') {
        return { success: false, code: 'CANCELLED', message: 'No Temp Stick was chosen' };
      }
      throw e;
    }

    this.device = device;
    this.name = device.name || '';
    device.addEventListener('gattserverdisconnected', this.onLinkDropped);

    const server = await withTimeout(device.gatt.connect(), timeoutMs, 'Bluetooth connection timed out');
    const service = await withTimeout(server.getPrimaryService(SERVICE_UUID), timeoutMs, 'Bluetooth service lookup timed out');

    this.reqChar = await service.getCharacteristic(REQ_UUID);
    this.respChar = await service.getCharacteristic(RESP_UUID);
    this.infoChar = await service.getCharacteristic(INFO_UUID);

    try {
      this.statusChar = await service.getCharacteristic(STATUS_UUID);
    } catch (e) {
      this.statusChar = null;
    }

    await this.respChar.startNotifications();
    this.respChar.addEventListener('characteristicvaluechanged', this.onResponsePacket);

    if (this.statusChar) {
      try {
        await this.statusChar.startNotifications();
        this.statusChar.addEventListener('characteristicvaluechanged', this.onStatusPacket);
      } catch (e) {
        // status pushes are a nicety; the heartbeat polls anyway
      }
    }

    const infoValue = await this.infoChar.readValue();
    const info = JSON.parse(new TextDecoder().decode(infoValue));

    this.sensorId = String(info.sid || '');
    this.mac = String(info.mac || '');
    if (!this.name) {
      this.name = String(info.name || '');
    }

    if (wantSid && normalizeSid(this.sensorId) !== wantSid) {
      const got = this.sensorId;
      await this.teardown('wrong_sensor', false);
      return { success: false, code: 'WRONG_SENSOR', message: `That is Temp Stick ${got}, not the one being set up` };
    }

    this.ready = true;

    return {
      success: true,
      transport: 'ble',
      sensorId: this.sensorId,
      mac: this.mac,
      address: device.id,
      name: this.name,
      mtu: 0
    };

  }

  // { path, type, data, skipResponse, timeoutMs, callback }
  //   -> { success: true, status, response } | { error: true, message, code }
  request(opts = {}) {

    return new Promise(resolve => {

      const finish = result => resolve(this.deliver(opts, result));

      if (!this.ready || !this.reqChar) {
        finish({ error: true, unreachable: true, code: 'NOT_CONNECTED', message: 'Not connected to a Temp Stick over Bluetooth' });
        return;
      }

      let path = typeof opts.path === 'string' ? opts.path : '/';
      if (!path.startsWith('/')) {
        path = '/' + path;
      }

      this.queue.push({
        id: this.nextId++,
        method: (typeof opts.type === 'string' ? opts.type : 'GET').toUpperCase(),
        path,
        data: (opts.data && typeof opts.data === 'object') ? opts.data : null,
        skipResponse: !!opts.skipResponse,
        timeoutMs: opts.timeoutMs || DEFAULT_REQUEST_TIMEOUT_MS,
        finish,
        timer: null
      });

      this.pump();

    });

  }

  async pump() {

    if (this.active || this.queue.length === 0) {
      return;
    }

    const r = this.queue.shift();
    this.active = r;

    this.rx = { chunks: [], size: 0, open: false, seq: 0 };

    r.timer = setTimeout(() => {
      if (this.active === r) {
        this.finishActive({ error: true, timeout: true, code: 'TIMEOUT', message: 'Request timed out - the Temp Stick did not answer over Bluetooth' });
      }
    }, r.timeoutMs);

    try {
      await this.send(r);
    } catch (e) {
      if (this.active === r) {
        this.finishActive({ error: true, code: 'WRITE_FAILED', message: (e && e.message) ? e.message : String(e) });
      }
    }

  }

  async send(r) {

    const message = { i: r.id, m: r.method, p: r.path };
    if (r.data) {
      message.d = r.data;
    }

    const bytes = new TextEncoder().encode(JSON.stringify(message));

    let off = 0;
    let seq = 0;
    let first = true;

    do {

      const n = Math.min(REQUEST_PAYLOAD_BYTES, bytes.length - off);
      const last = off + n >= bytes.length;

      const packet = new Uint8Array(n + 1);
      packet[0] = (first ? FRAME_FIRST : 0) | (last ? FRAME_LAST : 0) | (seq & FRAME_SEQ);
      packet.set(bytes.subarray(off, off + n), 1);

      if (this.active !== r || !this.reqChar) {
        return;
      }

      if (typeof this.reqChar.writeValueWithResponse === 'function') {
        await this.reqChar.writeValueWithResponse(packet);
      } else {
        await this.reqChar.writeValue(packet);
      }

      off += n;
      seq = (seq + 1) & FRAME_SEQ;
      first = false;

    } while (off < bytes.length);

    if (r.skipResponse && this.active === r) {
      this.finishActive({ success: true, message: 'Request dispatched', response: '' });
    }

  }

  onResponsePacket = event => {

    const view = event.target.value;
    const packet = new Uint8Array(view.buffer, view.byteOffset, view.byteLength);

    if (packet.length < 1) {
      return;
    }

    const hdr = packet[0];
    const first = (hdr & FRAME_FIRST) !== 0;
    const last = (hdr & FRAME_LAST) !== 0;
    const seq = hdr & FRAME_SEQ;

    const rx = this.rx;

    if (first) {
      rx.chunks = [];
      rx.size = 0;
      rx.open = true;
      rx.seq = seq;
    } else if (!rx.open) {
      return;
    } else if (seq !== ((rx.seq + 1) & FRAME_SEQ)) {
      console.log('ble response sequence gap', seq, rx.seq);
      rx.open = false;
      rx.chunks = [];
      rx.size = 0;
      return;
    } else {
      rx.seq = seq;
    }

    const payload = packet.slice(1);

    if (rx.size + payload.length > MAX_RESPONSE_BYTES) {
      rx.open = false;
      rx.chunks = [];
      rx.size = 0;
      return;
    }

    rx.chunks.push(payload);
    rx.size += payload.length;

    if (!last) {
      return;
    }

    rx.open = false;

    const all = new Uint8Array(rx.size);
    let pos = 0;
    for (const chunk of rx.chunks) {
      all.set(chunk, pos);
      pos += chunk.length;
    }
    rx.chunks = [];
    rx.size = 0;

    const r = this.active;
    if (!r) {
      return;
    }

    let msg;
    try {
      msg = JSON.parse(new TextDecoder().decode(all));
    } catch (e) {
      this.finishActive({ error: true, code: 'BAD_RESPONSE', message: 'Unreadable response from the Temp Stick' });
      return;
    }

    if (msg.i !== r.id) {
      return;   // late answer to an earlier (fire-and-forget) request
    }

    if (r.skipResponse) {
      return;   // already resolved when the write finished
    }

    const status = typeof msg.s === 'number' ? msg.s : 200;
    const body = typeof msg.b === 'string' ? msg.b : '';

    if (status >= 200 && status < 300) {
      this.finishActive({ success: true, status, response: body });
    } else {
      this.finishActive({ error: true, status, message: `HTTP ${status}`, response: body });
    }

  }

  onStatusPacket = event => {
    try {
      const status = new TextDecoder().decode(event.target.value);
      window.dispatchEvent(new CustomEvent('tempstick:ble-status', { detail: { status, sensorId: this.sensorId } }));
    } catch (e) {
      // ignore
    }
  }

  finishActive(result) {

    const r = this.active;
    if (!r) {
      return;
    }

    clearTimeout(r.timer);
    this.active = null;

    r.finish(result);
    this.pump();

  }

  failAll(code, message) {

    const pending = [];
    if (this.active) {
      clearTimeout(this.active.timer);
      pending.push(this.active);
      this.active = null;
    }
    pending.push(...this.queue);
    this.queue = [];

    pending.forEach(r => r.finish({ error: true, unreachable: true, code, message }));

  }

  // The sensor closed the link (restart, sleep, out of range).
  onLinkDropped = () => {

    const wasReady = this.ready;
    const sensorId = this.sensorId;
    const address = this.device ? this.device.id : '';

    this.resetState();
    this.failAll('NOT_CONNECTED', 'Bluetooth connection lost');

    if (wasReady) {
      window.dispatchEvent(new CustomEvent('tempstick:ble-disconnected', {
        detail: { reason: 'lost', sensorId, address }
      }));
    }

  }

  resetState() {
    this.ready = false;
    this.reqChar = null;
    this.respChar = null;
    this.infoChar = null;
    this.statusChar = null;
    this.rx = { chunks: [], size: 0, open: false, seq: 0 };
  }

  async teardown(reason, notify) {

    const device = this.device;
    const wasReady = this.ready;
    const sensorId = this.sensorId;

    if (device) {
      device.removeEventListener('gattserverdisconnected', this.onLinkDropped);
      if (this.respChar) {
        try { this.respChar.removeEventListener('characteristicvaluechanged', this.onResponsePacket); } catch (e) {}
      }
      if (this.statusChar) {
        try { this.statusChar.removeEventListener('characteristicvaluechanged', this.onStatusPacket); } catch (e) {}
      }
      try {
        if (device.gatt && device.gatt.connected) {
          device.gatt.disconnect();
        }
      } catch (e) {
        // ignore
      }
    }

    this.device = null;
    this.sensorId = '';
    this.mac = '';
    this.name = '';
    this.resetState();
    this.failAll('NOT_CONNECTED', 'Bluetooth connection ' + reason);

    if (notify && wasReady) {
      window.dispatchEvent(new CustomEvent('tempstick:ble-disconnected', {
        detail: { reason, sensorId, address: device ? device.id : '' }
      }));
    }

  }

  async disconnect(opts) {
    const wasConnected = this.ready;
    await this.teardown('requested', false);
    return this.deliver(opts, { success: true, wasConnected });
  }

}

let instance = null;

// The bridge, or null when this browser can't do Web Bluetooth.
export function webBluetoothBridge() {

  if (!webBluetoothAvailable()) {
    return null;
  }

  if (!instance) {
    instance = new WebBluetoothBridge();
  }

  return instance;

}
